import { createSelector } from 'reselect'
import { getHistoryQueryParams } from 'selectors/router'
import { Map } from 'immutable'

import { CURRENT_URI, CURRENT } from 'constants/featureSnapshots'
import { TEST } from 'utils/constants'

import { getIn, pickToJS } from 'utils/immutable'

const selectSnapshots = getIn(['feature', 'snapshots'])
const selectSnapshot = (state, { uri }) =>
  getIn(['feature', 'snapshots', uri], state)
const selectOriginalFeature = getIn(['feature', 'originalFeature', 'feature'])

const getSortedSnapshots = createSelector(selectSnapshots, (snapshots) =>
  snapshots.valueSeq().sortBy((snap) => snap.get('date'))
)

const selectNewestSnapshot = createSelector(getSortedSnapshots, (snapshots) =>
  snapshots.valueSeq().last()
)

const getCurrentVersion = createSelector(
  selectOriginalFeature,
  selectNewestSnapshot,
  (currentVersion, newestSnapshot) => {
    if (!currentVersion) return Map()

    const status = currentVersion.valueSeq().first().get('status')

    const useNewest = status === TEST && newestSnapshot

    const val = Map({
      status,
      date: useNewest ? newestSnapshot.get('date') : null,
      user: useNewest ? newestSnapshot.get('user') : null,
      uri: CURRENT_URI,
      id: CURRENT,
    })

    return val
  }
)

export const getSnapshotList = createSelector(
  getSortedSnapshots,
  getCurrentVersion,
  (snapshots, currentVersion) => {
    const status = currentVersion.get('status')
    const currentDate = currentVersion.get('date')

    const inTest = status === TEST

    if (!snapshots || snapshots.size === 0) return []

    const result =
      snapshots
        .valueSeq()
        .map((snapshot) => {
          const { id, uri, user, date, feature } = pickToJS(
            ['id', 'uri', 'user', 'date', 'feature'],
            snapshot
          )
          const { status } = feature
          if (inTest && date === currentDate) {
            return currentVersion.toJS()
          }
          return {
            status,
            date,
            user,
            uri,
            id,
          }
        })
        .toJS() || []

    if (!inTest) {
      result.push(currentVersion.toJS())
    }

    return result.reverse()
  }
)

export const getSelectedSnapshot = createSelector(
  selectSnapshot,
  getCurrentVersion,
  (_, ownProps) => ownProps.uri,
  (snapshot, currentVersion, uri) => {
    if (!currentVersion) return {}

    if (uri === CURRENT_URI) return currentVersion.toJS()

    if (!snapshot) return {}

    const { id, user, date, feature } = pickToJS(
      ['id', 'user', 'date', 'feature'],
      snapshot
    )
    const { status } = feature
    return {
      status,
      date,
      user,
      uri,
      id,
    }
  }
)

export const getRawSnapshotFeature = createSelector(
  selectSnapshot,
  (snapshot) => {
    if (!snapshot) return {}

    return pickToJS(['uri', 'feature'], snapshot)
  }
)

export const getCurrentSnapshotDate = createSelector(
  getHistoryQueryParams,
  selectSnapshots,
  (uri, snapshots) => {
    if (uri) return snapshots.getIn([uri, 'date'])
    return null
  }
)
